import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { massagesService } from '../services/massages';
import type { CreateMassageInput, UpdateMassageInput } from '../types/entities';
import { toast } from 'sonner';
import { apiClient } from '../services/api';

export function useMassages() {
  return useQuery({
    queryKey: ['massages'],
    queryFn: () => massagesService.getAll(),
  });
}

export function useMassage(id: string) {
  return useQuery({
    queryKey: ['massages', id],
    queryFn: () => massagesService.getById(id),
    enabled: !!id,
  });
}

export function useCreateMassage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateMassageInput) => massagesService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['massages'] });
      toast.success('Massage créé avec succès');
    },
    onError: (error) => {
      toast.error(apiClient.getErrorMessage(error));
    },
  });
}

export function useUpdateMassage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateMassageInput }) =>
      massagesService.update(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['massages'] });
      queryClient.invalidateQueries({ queryKey: ['massages', variables.id] });
      toast.success('Massage mis à jour');
    },
    onError: (error) => {
      toast.error(apiClient.getErrorMessage(error));
    },
  });
}

export function useDeleteMassage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => massagesService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['massages'] });
      toast.success('Massage supprimé');
    },
    onError: (error) => {
      toast.error(apiClient.getErrorMessage(error));
    },
  });
}
